import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cloneDeep } from 'lodash';
import { CrudFormItem } from '../../../utils/edit-form';
import { IconType } from '../../../utils/icon';
import { ButtonSize } from '../../../utils/button';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

/**
 * Component for managing field visibility in data detail view builder
 * Only supports visibility toggle, no delete functionality
 */
@Component({
  selector: 'rappider-data-detail-view-builder-fields',
  templateUrl: './data-detail-view-builder-fields.component.html',
  styleUrls: ['./data-detail-view-builder-fields.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DataDetailViewBuilderFieldsComponent),
      multi: true
    }
  ]
})
export class DataDetailViewBuilderFieldsComponent implements OnInit, ControlValueAccessor {
  @Output() populatedDataChange = new EventEmitter<Record<string, unknown>[]>();

  _value: CrudFormItem[] = [];
  populatedData: Record<string, unknown>[] = [];

  ButtonSize = ButtonSize;

  get value(): CrudFormItem[] {
    return this._value;
  }

  set value(value: CrudFormItem[]) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
    this.populateData();
  }

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    if (!this._value) {
      this._value = [];
    }
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: CrudFormItem[]): void {
    if (!value) {
      this._value = [];
    } else {
      // Ensure all fields have visible property set (default to true if undefined)
      this._value = cloneDeep(value).map(item => ({
        ...item,
        visible: item.visible !== undefined ? item.visible : true
      }));
      this.populateData();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Get visibility icon config based on visibility state
   */
  getVisibilityIconConfig(isVisible: boolean): { name: string; type: IconType } {
    return {
      name: isVisible ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash',
      type: IconType.FontAwesome
    };
  }

  /**
   * Check if field is visible
   */
  isFieldVisible(field: CrudFormItem): boolean {
    return field.visible === true;
  }

  /**
   * Toggle visibility of a field
   */
  onVisibilityToggle(index: number): void {
    if (!this._value || this._value[index] === undefined) {
      return;
    }

    const currentVisible = this._value[index].visible === true;
    const newValue = [...this._value];
    newValue[index] = {
      ...newValue[index],
      visible: !currentVisible
    };

    // Update value to trigger change detection and emit changes
    this._value = newValue;
    this.onChange(newValue);
    this.onTouched();
    this.populateData();
    this.cdr.detectChanges();
  }

  /**
   * Handle drag and drop to reorder fields
   */
  drop(event: CdkDragDrop<CrudFormItem[]>): void {
    if (this._value && event.previousIndex !== event.currentIndex) {
      const newValue = [...this._value];
      moveItemInArray(newValue, event.previousIndex, event.currentIndex);
      // Update index property to reflect new order (immutable update)
      const updatedValue = newValue.map((item, index) => ({
        ...item,
        index: index
      }));
      this.value = updatedValue;
    }
  }

  /**
   * Populate example data based on field types
   * Only includes visible fields
   */
  private populateData(): void {
    if (!this._value?.length) {
      this.populatedData = [];
      this.populatedDataChange.emit(this.populatedData);
      return;
    }

    const exampleData: Record<string, unknown> = {};
    this._value.forEach(item => {
      // Only include visible fields in preview (explicitly check for true)
      if (item.fieldName && item.visible === true) {
        exampleData[item.fieldName] = this.generateExampleValue(item);
      }
    });

    this.populatedData = [exampleData];
    this.populatedDataChange.emit(this.populatedData);
  }

  /**
   * Generate example value based on field type
   */
  private generateExampleValue(item: CrudFormItem): any {
    const fieldName = item.fieldName?.toLowerCase() || '';

    // Common field name patterns
    if (fieldName.includes('name') || fieldName.includes('title')) {
      return item.title || 'Example Name';
    }
    if (fieldName.includes('email')) {
      return 'example@email.com';
    }
    if (fieldName.includes('phone')) {
      return '+1 234 567 8900';
    }
    if (fieldName.includes('date')) {
      return new Date().toISOString().split('T')[0];
    }
    if (fieldName.includes('description') || fieldName.includes('note')) {
      return 'Example description text';
    }
    if (fieldName.includes('id')) {
      return '123';
    }
    if (fieldName.includes('status')) {
      return 'Active';
    }
    if (fieldName.includes('amount') || fieldName.includes('price') || fieldName.includes('cost')) {
      return 99.99;
    }
    if (fieldName.includes('count') || fieldName.includes('quantity') || fieldName.includes('number')) {
      return 42;
    }
    if (fieldName.includes('active') || fieldName.includes('enabled')) {
      return true;
    }

    // Default based on type
    return 'Example Value';
  }
}

